/**
 * Карточка компании-клиента (Stripe/Linear-style).
 *
 * Лёгкий контейнер без тяжёлых заливок — заголовок, реквизиты в две
 * колонки, футер с действиями. Статус — точкой + текст с dropdown'ом.
 */

import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Pencil, Trash2, type LucideIcon } from "lucide-react";
import type { ClientInfo, ClientStatus } from "@/services/api";
import { RISK_STYLES } from "@/constants/riskLevels";
import { cn } from "@/utils";

interface ClientCardProps {
  client: ClientInfo;
  onStatusChange?: (clientId: number, newStatus: ClientStatus) => void;
  isStatusChanging?: boolean;
  onDelete?: (clientId: number) => void;
  isDeleting?: boolean;
  onEdit?: (clientId: number) => void;
}

const STATUS_CONFIG: Record<ClientStatus, { label: string; color: string }> = {
  ACTIVE: { label: "Активен", color: RISK_STYLES.A.color },
  NOT_ACTIVATED: { label: "Не активирован", color: RISK_STYLES.B.color },
  SUSPENDED: { label: "Приостановлен", color: RISK_STYLES.C.color },
  BLOCKED: { label: "Заблокирован", color: RISK_STYLES.D.color },
};

const STATUS_OPTIONS: { value: ClientStatus; label: string }[] = [
  { value: "ACTIVE", label: "Активен" },
  { value: "NOT_ACTIVATED", label: "Не активирован" },
  { value: "SUSPENDED", label: "Приостановлен" },
  { value: "BLOCKED", label: "Заблокирован" },
];

function formatDate(dateString: string | null): string {
  if (!dateString) return "—";
  try {
    return new Date(dateString).toLocaleDateString("ru-RU", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

const ClientCard: React.FC<ClientCardProps> = ({
  client,
  onStatusChange,
  isStatusChanging = false,
  onDelete,
  isDeleting = false,
  onEdit,
}) => {
  const { organization, users_count, active_users_count } = client;
  const status = STATUS_CONFIG[organization.status];
  const displayName = organization.short_name || organization.full_name;

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isDropdownOpen]);

  const handleStatusSelect = (newStatus: ClientStatus) => {
    setIsDropdownOpen(false);
    if (newStatus !== organization.status) {
      onStatusChange?.(organization.id, newStatus);
    }
  };

  return (
    <div className="bg-surface-elevated border border-line rounded-card overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-line">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h3 className="text-fs-lg font-semibold text-ink-1 truncate" title={displayName}>
              {displayName}
            </h3>
            {organization.short_name && organization.short_name !== organization.full_name && (
              <p className="mt-0.5 text-fs-xs text-ink-3 truncate" title={organization.full_name}>
                {organization.full_name}
              </p>
            )}
          </div>

          {/* Статус с dropdown'ом */}
          <div className="relative shrink-0" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setIsDropdownOpen((o) => !o)}
              disabled={isStatusChanging}
              className={cn(
                "inline-flex items-center gap-1.5 h-8 pl-3 pr-2 rounded-pill border border-line",
                "bg-surface-canvas text-ink-1 text-fs-xs font-medium",
                "hover:bg-surface-hover transition-colors",
                isStatusChanging && "opacity-50 cursor-wait",
              )}
            >
              <span
                className="w-1.5 h-1.5 rounded-full shrink-0"
                style={{ backgroundColor: status.color }}
                aria-hidden
              />
              {isStatusChanging ? "Изменение…" : status.label}
              <ChevronDown
                size={14}
                strokeWidth={1.75}
                className={cn("text-ink-3 transition-transform", isDropdownOpen && "rotate-180")}
              />
            </button>

            {isDropdownOpen && (
              <div className="absolute right-0 top-[calc(100%+6px)] z-30 min-w-[200px] bg-surface-elevated border border-line rounded-card shadow-shell-md py-1">
                {STATUS_OPTIONS.map((option) => {
                  const cfg = STATUS_CONFIG[option.value];
                  const isCurrent = option.value === organization.status;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleStatusSelect(option.value)}
                      className={cn(
                        "w-full flex items-center gap-2 px-3 py-1.5 text-left text-fs-sm transition-colors",
                        "hover:bg-surface-hover",
                        isCurrent ? "text-ink-1 font-medium" : "text-ink-2",
                      )}
                    >
                      <span
                        className="w-1.5 h-1.5 rounded-full shrink-0"
                        style={{ backgroundColor: cfg.color }}
                        aria-hidden
                      />
                      {option.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Реквизиты */}
      <dl className="px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
        <KeyValue label="ИНН" value={<span className="font-mono tabular-nums">{organization.inn}</span>} />
        <KeyValue label="ОГРН" value={<span className="font-mono tabular-nums">{organization.ogrn || "—"}</span>} />
        <KeyValue
          label="Договор"
          value={
            organization.contract_number ? (
              <>
                № <span className="font-mono tabular-nums">{organization.contract_number}</span>
                {organization.contract_date && (
                  <span className="text-ink-3"> от {formatDate(organization.contract_date)}</span>
                )}
              </>
            ) : (
              <span className="text-ink-4">Не указан</span>
            )
          }
        />
        <KeyValue
          label="Пользователи"
          value={
            <>
              <span className="font-mono tabular-nums">{active_users_count}</span>
              <span className="text-ink-3"> активных из </span>
              <span className="font-mono tabular-nums">{users_count}</span>
            </>
          }
        />
      </dl>

      {/* Footer */}
      <div className="px-5 py-3 border-t border-line flex items-center justify-between">
        <span className="text-fs-xs text-ink-3">Зарегистрирован: {formatDate(organization.created_at)}</span>
        <div className="flex items-center gap-1">
          {onEdit && (
            <FooterButton onClick={() => onEdit(organization.id)} icon={Pencil}>
              Редактировать
            </FooterButton>
          )}
          {onDelete && (
            <FooterButton onClick={() => onDelete(organization.id)} icon={Trash2} disabled={isDeleting} danger>
              {isDeleting ? "Удаление…" : "Удалить"}
            </FooterButton>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientCard;

// ─── Sub-components ─────────────────────────────────────────────────────────

const KeyValue: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div>
    <dt className="text-fs-2xs font-medium uppercase tracking-[0.04em] text-ink-3">{label}</dt>
    <dd className="mt-1 text-fs-sm text-ink-1">{value}</dd>
  </div>
);

const FooterButton: React.FC<{
  icon: LucideIcon;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
  children: React.ReactNode;
}> = ({ icon: Icon, onClick, disabled, danger, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={cn(
      "inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-fs-xs font-medium transition-colors",
      "text-ink-2 hover:text-ink-1 hover:bg-surface-hover",
      "disabled:opacity-50 disabled:cursor-not-allowed",
      danger && "hover:text-[color:var(--risk-d)]",
    )}
    style={danger ? ({ "--risk-d": RISK_STYLES.D.color } as React.CSSProperties) : undefined}
  >
    <Icon size={13} strokeWidth={1.75} />
    {children}
  </button>
);
